import type { PractitionerWithRelations } from '@borradh-workspace/api-client/types';

import type { PractitionerServiceAssignment } from '../service-form/service-form-payload';

/** The services this team member already offers. */
export function practitionerServiceIds(
  practitioner: PractitionerWithRelations
): string[] {
  return practitioner.services?.map((link) => link.serviceId) ?? [];
}

/** How many of `serviceIds` this team member already offers. */
export function countAlreadyAssigned(
  practitioner: PractitionerWithRelations,
  serviceIds: readonly string[]
): number {
  const existing = new Set(practitionerServiceIds(practitioner));
  return serviceIds.filter((id) => existing.has(id)).length;
}

/**
 * Fold `serviceIds` INTO each chosen team member's existing set.
 *
 * ADDITIVE, and that is the whole reason this helper exists: the endpoint is
 * `PUT practitioners/:id/services`, which REPLACES the practitioner's set. A
 * body carrying only the selected services would silently unassign everything
 * else they offer — and a practitioner with no services is not bookable. So the
 * body is always "what they had" ∪ "what was selected", in that order, so the
 * server sees a stable list and the UI stays in the order the operator knows.
 *
 * A team member who already offers every selected service produces no request:
 * nothing would change, and a no-op write still costs a round trip and a toast.
 */
export function buildBulkServiceAssignments({
  serviceIds,
  practitioners,
  selectedPractitionerIds,
}: {
  serviceIds: readonly string[];
  practitioners: PractitionerWithRelations[];
  selectedPractitionerIds: readonly string[];
}): PractitionerServiceAssignment[] {
  const chosen = new Set(selectedPractitionerIds);
  const assignments: PractitionerServiceAssignment[] = [];

  for (const practitioner of practitioners) {
    if (!chosen.has(practitioner.id)) continue;
    const existing = practitionerServiceIds(practitioner);
    const have = new Set(existing);
    const missing = serviceIds.filter((id) => !have.has(id));
    if (missing.length === 0) continue;
    assignments.push({
      practitionerId: practitioner.id,
      // De-duplicate the existing links too: a doubled join row must not turn
      // into two ids in the body, which the server would reject.
      serviceIds: [...new Set([...existing, ...missing])],
    });
  }

  return assignments;
}
